<template>
  <div class="container-fluid">
    <div class="row">
      <div class="col-md-12">
        <div class="card">
          <div class="card-header">
            <h1>Brand Settings</h1>
            <p>Manage brand-specific settings with key-value pairs</p>
          </div>
          <div class="card-body">
            <div v-if="loading" class="text-center">
              <div class="spinner-border" role="status">
                <span class="sr-only">Loading...</span>
              </div>
            </div>

            <div v-else>
              <!-- Add New Setting Form -->
              <div class="section">
                <h3>Add New Setting</h3>
                <form @submit.prevent="addSetting" class="row">
                  <div class="col-md-3">
                    <div class="form-group">
                      <label for="newSettingKey">Setting Key</label>
                      <input
                        type="text"
                        id="newSettingKey"
                        v-model="newSetting.key"
                        class="form-control"
                        placeholder="e.g., max_pickls_per_user"
                        required
                      >
                    </div>
                  </div>
                  <div class="col-md-3">
                    <div class="form-group">
                      <label for="newSettingLabel">Label</label>
                      <input
                        type="text"
                        id="newSettingLabel"
                        v-model="newSetting.label"
                        class="form-control"
                        placeholder="e.g., Maximum Pickls Per User"
                        required
                      >
                    </div>
                  </div>
                  <div class="col-md-3">
                    <div class="form-group">
                      <label for="newSettingValue">Value</label>
                      <input
                        type="text"
                        id="newSettingValue"
                        v-model="newSetting.value"
                        class="form-control"
                        placeholder="e.g., 10"
                        required
                      >
                    </div>
                  </div>
                  <div class="col-md-3">
                    <div class="form-group">
                      <label>&nbsp;</label>
                      <button
                        type="submit"
                        class="btn btn-primary btn-block"
                        :disabled="addingSetting"
                      >
                        <span v-if="addingSetting" class="spinner-border spinner-border-sm mr-2" role="status"></span>
                        {{ addingSetting ? 'Adding...' : 'Add Setting' }}
                      </button>
                    </div>
                  </div>
                </form>
              </div>

              <!-- Settings List -->
              <div class="section">
                <h3>Current Settings</h3>
                <div v-if="settings.length === 0" class="text-center">
                  <p class="text-muted">No settings found for this brand.</p>
                </div>
                <div v-else class="table-responsive">
                  <table class="table table-striped">
                    <thead>
                      <tr>
                        <th>Key</th>
                        <th>Label</th>
                        <th>Value</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="setting in settings" :key="setting.id">
                        <td>
                          <span v-if="!setting.editing">{{ setting.key }}</span>
                          <input
                            v-else
                            type="text"
                            v-model="setting.editKey"
                            class="form-control"
                            required
                          >
                        </td>
                        <td>
                          <span v-if="!setting.editing">{{ setting.label }}</span>
                          <input
                            v-else
                            type="text"
                            v-model="setting.editLabel"
                            class="form-control"
                            required
                          >
                        </td>
                        <td>
                          <span v-if="!setting.editing">{{ setting.value }}</span>
                          <input
                            v-else
                            type="text"
                            v-model="setting.editValue"
                            class="form-control"
                            required
                          >
                        </td>
                        <td>
                          <div v-if="!setting.editing">
                            <button
                              @click="startEdit(setting)"
                              class="btn btn-sm btn-outline-primary mr-2"
                            >
                              Edit
                            </button>
                            <button
                              @click="deleteSetting(setting.id)"
                              class="btn btn-sm btn-outline-danger"
                              :disabled="deletingSetting === setting.id"
                            >
                              <span v-if="deletingSetting === setting.id" class="spinner-border spinner-border-sm mr-1" role="status"></span>
                              Delete
                            </button>
                          </div>
                          <div v-else>
                            <button
                              @click="saveEdit(setting)"
                              class="btn btn-sm btn-success mr-2"
                              :disabled="updatingSetting === setting.id"
                            >
                              <span v-if="updatingSetting === setting.id" class="spinner-border spinner-border-sm mr-1" role="status"></span>
                              Save
                            </button>
                            <button
                              @click="cancelEdit(setting)"
                              class="btn btn-sm btn-secondary"
                            >
                              Cancel
                            </button>
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'BrandSettings',
  data: function () {
    return {
      loading: true,
      addingSetting: false,
      updatingSetting: null,
      deletingSetting: null,
      brandId: '',
      settings: [],
      newSetting: {
        key: '',
        label: '',
        value: ''
      }
    }
  },
  mounted () {
    this.brandId = this.$route.params.brandId
    this.fetchSettings()
  },
  methods: {
    async fetchSettings () {
      try {
        this.loading = true
        const response = await this.$store.dispatch('getBrandSettings', this.brandId)

        if (response.body && response.body.data) {
          this.settings = response.body.data.map(setting => ({
            ...setting,
            editing: false,
            editKey: setting.key,
            editLabel: setting.label,
            editValue: setting.value
          }))
        }
      } catch (error) {
        console.error('Error fetching brand settings:', error)
        this.$notify({
          type: 'error',
          text: 'Failed to load brand settings.'
        })
      } finally {
        this.loading = false
      }
    },

    async addSetting () {
      try {
        this.addingSetting = true

        const payload = {
          brandId: this.brandId,
          setting: this.newSetting
        }

        await this.$store.dispatch('addBrandSetting', payload)

        this.$notify({
          type: 'success',
          text: 'Setting added successfully!'
        })

        // Reset form
        this.newSetting = {
          key: '',
          label: '',
          value: ''
        }

        // Refresh settings list
        await this.fetchSettings()
      } catch (error) {
        console.error('Error adding setting:', error)
        this.$notify({
          type: 'error',
          text: 'Failed to add setting'
        })
      } finally {
        this.addingSetting = false
      }
    },

    startEdit (setting) {
      setting.editing = true
      setting.editKey = setting.key
      setting.editLabel = setting.label
      setting.editValue = setting.value
    },

    cancelEdit (setting) {
      setting.editing = false
      setting.editKey = setting.key
      setting.editLabel = setting.label
      setting.editValue = setting.value
    },

    async saveEdit (setting) {
      try {
        this.updatingSetting = setting.id

        const payload = {
          brandId: this.brandId,
          settingId: setting.id,
          setting: {
            key: setting.editKey,
            label: setting.editLabel,
            value: setting.editValue
          }
        }

        await this.$store.dispatch('updateBrandSetting', payload)

        // Update local data
        setting.key = setting.editKey
        setting.label = setting.editLabel
        setting.value = setting.editValue
        setting.editing = false

        this.$notify({
          type: 'success',
          text: 'Setting updated successfully!'
        })
      } catch (error) {
        console.error('Error updating setting:', error)
        this.$notify({
          type: 'error',
          text: 'Failed to update setting'
        })
      } finally {
        this.updatingSetting = null
      }
    },

    async deleteSetting (settingId) {
      if (!confirm('Are you sure you want to delete this setting?')) {
        return
      }

      try {
        this.deletingSetting = settingId

        const payload = {
          brandId: this.brandId,
          settingId: settingId
        }

        await this.$store.dispatch('deleteBrandSetting', payload)

        // Remove from local array
        this.settings = this.settings.filter(setting => setting.id !== settingId)

        this.$notify({
          type: 'success',
          text: 'Setting deleted successfully!'
        })
      } catch (error) {
        console.error('Error deleting setting:', error)
        this.$notify({
          type: 'error',
          text: 'Failed to delete setting'
        })
      } finally {
        this.deletingSetting = null
      }
    }
  }
}
</script>

<style scoped>
.card {
  border: none;
  box-shadow: 0 0 20px rgba(0, 0, 0, 0.1);
  border-radius: 10px;
}

.card-header {
  background: linear-gradient(135deg, #96b363 0%, #6a8f2a 100%);
  color: white;
  border-radius: 10px 10px 0 0 !important;
  padding: 2rem;
}

.card-header h1 {
  margin: 0;
  font-size: 2rem;
  font-weight: 600;
}

.card-body {
  padding: 2rem;
}

.section {
  margin-bottom: 3rem;
  padding-bottom: 2rem;
  border-bottom: 1px solid #e9ecef;
}

.section:last-child {
  border-bottom: none;
}

.section h3 {
  color: #333;
  margin-bottom: 1.5rem;
  font-weight: 600;
  border-left: 4px solid #007bff;
  padding-left: 1rem;
}

.form-group {
  margin-bottom: 1.5rem;
}

.form-group label {
  font-weight: 600;
  color: #333;
  margin-bottom: 0.5rem;
  font-size: 0.9rem;
}

.btn {
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 600;
  transition: all 0.3s ease;
}

.btn-primary {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border: none;
}

.btn-primary:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
}

.btn-success {
  background: linear-gradient(135deg, #28a745 0%, #20c997 100%);
  border: none;
}

.btn-success:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(40, 167, 69, 0.4);
}

.btn-danger {
  background: linear-gradient(135deg, #dc3545 0%, #fd7e14 100%);
  border: none;
}

.btn-danger:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(220, 53, 69, 0.4);
}

.table {
  background: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}

.table th {
  background: #f8f9fa;
  border: none;
  font-weight: 600;
  color: #333;
  padding: 1rem;
}

.table td {
  padding: 1rem;
  vertical-align: middle;
  border: none;
  border-bottom: 1px solid #e9ecef;
}

.spinner-border {
  width: 1rem;
  height: 1rem;
}

@media (max-width: 768px) {
  .card-header {
    padding: 1.5rem;
  }

  .card-header h1 {
    font-size: 1.5rem;
  }

  .card-body {
    padding: 1.5rem;
  }

  .section {
    margin-bottom: 2rem;
    padding-bottom: 1.5rem;
  }

  .table-responsive {
    font-size: 0.9rem;
  }
 }
</style>
